import { isRunningInNativeApp, native } from '@muzly/shared';
import type { NativeInvokeOptions, ShareResult } from '@muzly/shared';

import { LINK_ROUTE } from '../link';

/**
 * 결과 공유 — 링크 조립과 보내기 분기의 정본(화면은 이 두 함수를 부르기만 한다).
 */

/**
 * 공유 링크 — `${host}/link?to=%2Fshare%2F<analyId>`. 유니버설 링크가 안 걸리는 곳(인앱
 * 브라우저)에서도 게이트웨이(`LINK_ROUTE`)가 앱 열기를 시도한다. `to` 는 인코딩한다 —
 * id 의 콜론이 쿼리 안에서 다른 뜻이 되지 않도록.
 */
export const shareLinkOf = (analyId: string, host: string): string =>
  `${host.replace(/\/+$/, '')}${LINK_ROUTE}?to=${encodeURIComponent(`/share/${analyId}`)}`;

/** 공유 텍스트 하나 — 한 줄 문구 + 링크. 닉네임·프로필은 넣지 않는다. */
export const shareTextOf = (line: string, link: string): string =>
  `${line}\n${link}`;

/**
 * 시트는 사용자가 닫을 때까지 응답이 없다. 기본 옵션(5초×3회)으로 부르면 시트가 다시
 * 열린다 — `useProfileImage` 와 같은 사용자 대기 RPC 옵션.
 */
export const SHARE_INVOKE_OPTIONS: NativeInvokeOptions = {
  timeout: 120_000,
  retries: 1,
};

export interface ShareDeps {
  isNative: () => boolean;
  invoke: (
    params: { text: string },
    options: NativeInvokeOptions,
  ) => Promise<ShareResult>;
  copy: (text: string) => Promise<boolean>;
}

/** 복사에 성공했을 때만 true. 다른 feature(`more`)의 헬퍼를 가져오지 않고 같은 방식으로 둔다. */
const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const defaultDeps: ShareDeps = {
  isNative: isRunningInNativeApp,
  invoke: (params, options) => native.invoke('share', params, options),
  copy: copyText,
};

/**
 * 네이티브면 공유 시트, 아니거나 브리지가 실패하면 클립보드.
 * 시트를 닫은 것(`shared:false`)은 실패가 아니라 사용자의 선택이다 — `'shared'` 로 접어
 * 토스트를 내지 않는다.
 */
export const shareResult = async (
  text: string,
  deps: ShareDeps = defaultDeps,
): Promise<'shared' | 'copied' | 'failed'> => {
  if (deps.isNative()) {
    try {
      await deps.invoke({ text }, SHARE_INVOKE_OPTIONS);
      return 'shared';
    } catch {
      // 브리지 실패 — 아래 클립보드로 내려간다.
    }
  }
  return (await deps.copy(text)) ? 'copied' : 'failed';
};
